/**
 * The guided LLM judge (PR4) — the ONLY model opinion in the cockpit.
 *
 * Everything it renders is VIOLET, and the colour is load-bearing: orange means the cockpit
 * computed it, plain means it came verbatim from the database, violet means a model has an
 * OPINION about it. A reader must never have to guess which of the three they are looking at.
 *
 * It runs ON DEMAND (Jaume, 2026-09-01): the spend and the hallucination risk both belong to
 * a human decision, so nothing is judged until someone presses the button.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchJudgement, runJudge, type Judgement, type JudgeSection, type JudgeVerdict,
} from '@/services/productCockpitService';

const VERDICT_LABEL: Record<JudgeVerdict, string> = {
  complete: 'context complete',
  minor_gaps: 'minor context gaps',
  major_gaps: 'MAJOR context gaps',
  // NOT a fourth severity: the judge asserted a gap that nothing surviving validation
  // supports. Rendering it blank made a rejected assessment read as "no gaps found".
  indeterminate: 'verdict unsupported — nothing to stand on',
};

const JUDGE_TIP = 'LLM judge — a guided review of the generation CONTEXT against an editable '
  + 'per-section recipe. It may only report elements listed in that recipe, every finding must '
  + 'cite its evidence, and anything off-recipe is discarded and counted. It is a model OPINION, '
  + 'never a measurement, and it never judges the writing itself.';

/** One section's verdict, with its cited findings. */
export function JudgeSectionBlock({ section }: { section: JudgeSection }) {
  return (
    <div className="pmc-jblock">
      <div className="pmc-jhd">
        <span className="pmc-sbadge pmc-judge" data-tip={JUDGE_TIP}>LLM judge</span>
        <span className={`pmc-jverdict pmc-jv-${section.verdict}`}>{VERDICT_LABEL[section.verdict]}</span>
        <span className="pmc-small pmc-mute">context review vs the section’s recipe — opinion, not measurement</span>
      </div>
      {section.missing.map((m, i) => (
        <div className="pmc-jrow" key={`m-${i}`}>
          <span className={`pmc-jtag pmc-${m.severity}`}>missing · {m.severity}</span>
          <span>
            <b>{m.element}</b><br />
            <span className="pmc-small pmc-mute">{m.evidence}</span>
          </span>
        </div>
      ))}
      {section.weak.map((w, i) => (
        <div className="pmc-jrow" key={`w-${i}`}>
          <span className="pmc-jtag pmc-weak-tag">weak</span>
          <span>
            <b>{w.element}</b>{w.why ? <span className="pmc-small"> — {w.why}</span> : null}<br />
            <span className="pmc-small pmc-mute">{w.evidence}</span>
          </span>
        </div>
      ))}
      {section.verdict_note && (
        <div className="pmc-small pmc-jindet">{section.verdict_note}</div>
      )}
      {section.note && (
        <div className="pmc-small pmc-jnote"><b className="pmc-mute">judge note · </b>{section.note}</div>
      )}
    </div>
  );
}

interface JudgePanelProps {
  siteId: string;
  productRef: string;
  /** so each section panel can show its own verdict */
  onLoaded?: (j: Judgement | null) => void;
}

export function JudgePanel({ siteId, productRef, onLoaded }: JudgePanelProps) {
  const [judgement, setJudgement] = useState<Judgement | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [readError, setReadError] = useState<string | null>(null);

  // A slow initial GET could land AFTER a POST and overwrite the fresh result with an older
  // judgement, or with null. Every write is stamped, and a stale one is dropped.
  const seqRef = useRef(0);
  const apply = useCallback((seq: number, j: Judgement | null) => {
    if (seq < seqRef.current) return;   // superseded by a newer answer
    seqRef.current = seq;
    setJudgement(j);
    onLoaded?.(j);
  }, [onLoaded]);

  useEffect(() => {
    const ac = new AbortController();
    const seq = 1;
    fetchJudgement(siteId, productRef, ac.signal)
      .then((j) => apply(seq, j))
      .catch((err) => {
        if ((err as { name?: string })?.name === 'AbortError') return;
        // "there is no judgement yet" comes back as data:null, NOT as an error. So an error
        // here is a real read failure (before the migration has run, for instance) and
        // swallowing it would quietly present "not judged" for "could not tell".
        setReadError((err as Error)?.message ?? String(err));
      });
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, productRef]);

  const run = useCallback(async () => {
    setRunning(true);
    setError(null);
    try {
      // a judge result always supersedes an in-flight initial read
      const j = await runJudge(siteId, productRef, { force: !!judgement });
      apply(2, j);
    } catch (err) {
      setError((err as Error)?.message ?? String(err));
    } finally {
      setRunning(false);
    }
  }, [siteId, productRef, judgement, apply]);

  const s = judgement?.summary;

  return (
    <div className="pmc-judgebar">
      <button
        type="button"
        className="pmc-judgebtn"
        disabled={running}
        data-tip={`${JUDGE_TIP} Runs ON DEMAND (~30–60 s, a few cents per product) — nothing is judged unless you ask.`}
        onClick={run}
      >
        {running ? '⚖ judging… (~60 s)' : judgement ? '⚖ re-run LLM judge' : '⚖ run LLM judge'}
      </button>

      {s && (
        <span className="pmc-small">
          <span className="pmc-sbadge pmc-judge" data-tip={JUDGE_TIP}>LLM judge</span>
          <span className="pmc-mute">
            {` ${s.major_gaps} of ${s.sections_judged} sections with major context gaps · ${s.minor_gaps} minor`}
            {s.indeterminate ? ` · ${s.indeterminate} unsupported` : ''}
            {` · ${judgement?.model} · ${String(judgement?.created_at ?? '').slice(0, 10)}`}
            {judgement?.cost_usd != null ? ` · $${Number(judgement.cost_usd).toFixed(4)}` : ''}
          </span>
        </span>
      )}

      {/*
        ⛔ WHAT WAS NOT JUDGED BELONGS BESIDE THE COUNT. The backend persists
        summary.sections_excluded with each reason, and the panel showed only gap counts —
        so a partially judged generation read as a clean bill over sections nobody looked
        at. `sections_judged` above is the denominator; this says what is missing from it.
      */}
      {!!judgement?.summary?.sections_excluded?.length && (
        <span
          className="pmc-small pmc-syn"
          data-tip="These sections were not judged at all — their prompt was never archived, or no recipe covers them. Their absence from the verdicts above is NOT a finding about them."
        >
          {`⚠ ${judgement.summary.sections_excluded.length} section${judgement.summary.sections_excluded.length === 1 ? '' : 's'} NOT judged: `}
          {judgement.summary.sections_excluded
            .map((x) => `${x.name.replace(/_/g, ' ')} (${x.reason})`)
            .join(' · ')}
        </span>
      )}

      {/* a filter the reader cannot see is a filter the reader cannot trust */}
      {!!judgement?.discarded_count && (
        <span className="pmc-small pmc-syn" data-tip="The judge produced findings that were NOT in the section's recipe, or gave no evidence for them. Those were discarded by validation — this is how many.">
          {`⚠ ${judgement.discarded_count} off-recipe finding${judgement.discarded_count === 1 ? '' : 's'} discarded`}
        </span>
      )}

      {judgement?.stale === true && (
        <span className="pmc-small pmc-jstale" data-tip="This verdict was computed over DIFFERENT evidence than the page shows today — the product has been enriched or regenerated since. The findings do not describe what is on screen. Re-run to judge the current evidence.">
          ⚠ judged different evidence — re-run to judge what is on screen now
        </span>
      )}

      {judgement && judgement.staleness_known === false && (
        <span className="pmc-small pmc-mute" data-tip="Today's evidence could not be read (the prompt archive is unavailable), so whether this verdict still describes the page cannot be determined. It is not claimed to be current.">
          freshness unknown — today’s evidence could not be read
        </span>
      )}

      {judgement?.reused && (
        <span className="pmc-small pmc-mute">reused — the evidence has not changed since the last run</span>
      )}

      {error && <span className="pmc-small pmc-jerr">judge failed: {error}</span>}

      {readError && (
        <span className="pmc-small pmc-jerr">
          could not read a stored judgement: {readError}
        </span>
      )}
    </div>
  );
}
